// @ts-nocheck - untyped
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  Stack,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { notify } from '../../../lib/notify';
import ServiceNowService from '../../../Services/ServiceNowService';
import { getUserData } from '../../../Utils/tokenUtil';
import './WebScraper.scss';

const CreateIncidentDialog = ({ open, onClose, job, onIncidentCreated }) => {
  const [taskNumber, setTaskNumber] = useState('');
  const [deadline, setDeadline] = useState(null);
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [createdIncident, setCreatedIncident] = useState(null);

  const extractTaskNumber = fileName => {
    if (!fileName) return '';
    const match = fileName.match(/\d{6,}/);
    return match ? match[0] : '';
  };

  const addBusinessDays = (startDate, daysToAdd) => {
    const currentDate = new Date(startDate);
    let addedDays = 0;

    while (addedDays < daysToAdd) {
      currentDate.setDate(currentDate.getDate() + 1);
      const dayOfWeek = currentDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        addedDays++;
      }
    }

    return currentDate;
  };

  const calculateBusinessDays = (fromDate, toDate) => {
    if (!fromDate || !toDate) return 0;

    let count = 0;
    const currentDate = new Date(fromDate);
    const endDate = new Date(toDate);

    while (currentDate < endDate) {
      const dayOfWeek = currentDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        count++;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return count;
  };

  const formatEmailToName = email => {
    if (!email) return 'IPX User';
    const [firstRaw = '', rest = ''] = email.split('.');
    const [lastRaw = ''] = rest.split('@');

    const firstName = firstRaw ? firstRaw[0].toUpperCase() + firstRaw.slice(1) : '';
    const lastName = lastRaw ? lastRaw[0].toUpperCase() + lastRaw.slice(1) : '';

    return `${firstName} ${lastName}`.trim() || 'IPX User';
  };

  const generateDefaultDescription = (country, taskNum, deadlineDate, userEmail) => {
    const userName = formatEmailToName(userEmail);

    return `Dear colleagues,

Please find attached the post-payment list and update Argos at your earliest convenience.

This is the corresponding task number in Argos:
• ${country} - ${taskNum}

Thank you in advance for your kind assistance.

Sincerely,
${userName}`;
  };

  useEffect(() => {
    if (open && job) {
      const extractedTaskNumber = extractTaskNumber(job.fileName);
      setTaskNumber(extractedTaskNumber);

      const defaultDeadline = addBusinessDays(new Date(), 3);
      setDeadline(defaultDeadline);

      const user = getUserData();
      const userEmail = user?.email || user?.displayName || '';
      const defaultDesc = generateDefaultDescription(
        job.country,
        extractedTaskNumber,
        defaultDeadline,
        userEmail
      );
      setDescription(defaultDesc);

      setErrors({});
      setCreatedIncident(null);
    }
    // eslint-disable-next-line
  }, [open, job]);

  useEffect(() => {
    if (job && taskNumber && deadline) {
      const user = getUserData();
      const userEmail = user?.email || user?.displayName || '';
      const newDesc = generateDefaultDescription(job.country, taskNumber, deadline, userEmail);
      setDescription(newDesc);
    }
    // eslint-disable-next-line
  }, [taskNumber, deadline, job]);

  const handleTaskNumberChange = value => {
    setTaskNumber(value);
    if (errors.taskNumber) {
      const newErrors = { ...errors };
      delete newErrors.taskNumber;
      setErrors(newErrors);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    if (!taskNumber || taskNumber.trim() === '') {
      newErrors.taskNumber = 'Task number is required';
      isValid = false;
    }

    if (!deadline) {
      newErrors.deadline = 'Deadline is required';
      isValid = false;
    }

    if (!description || description.trim() === '') {
      newErrors.description = 'Description is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleCreateIncident = async () => {
    if (!validateForm()) {
      notify.warning('Please fix validation errors');
      return;
    }

    setLoading(true);

    try {
      const taskData = [{ country: job.country, taskNumber }];
      const response = await ServiceNowService.createIncident(
        job._id,
        taskData,
        description,
        deadline
      );

      if (response.data.success) {
        const incidentData = {
          incidentNumber: response.data.incidentNumber,
          incidentSysId: response.data.incidentSysId,
          incidentUrl: response.data.incidentUrl,
          dueDate: response.data.dueDate,
        };

        setCreatedIncident(incidentData);

        if (onIncidentCreated) {
          onIncidentCreated(response.data);
        }

        notify.success(`Incident ${response.data.incidentNumber} created`);
      } else {
        notify.error(response.data.message || 'Failed to create incident');
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to create incident';
      notify.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenIncident = () => {
    if (createdIncident?.incidentUrl) {
      window.open(createdIncident.incidentUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleClose = () => {
    if (!loading) {
      setTaskNumber('');
      setDeadline(null);
      setDescription('');
      setErrors({});
      setCreatedIncident(null);
      onClose();
    }
  };

  if (!job) return null;

  const businessDaysCount = deadline ? calculateBusinessDays(new Date(), deadline) : 3;

  const totalRows = (job.successRows || 0) + (job.failedRows || 0);
  const successRate = totalRows > 0 ? ((job.successRows || 0) / totalRows) * 100 : 0;
  const isSuccessRateBlocked = successRate < 40;
  const isSuccessRateWarning = successRate >= 40 && successRate < 60;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle className="ws-cid-title">
          <Typography variant="h6">Create ServiceNow Incident</Typography>
          <IconButton onClick={handleClose} disabled={loading} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers className="ws-cid-content">
          {createdIncident && (
            <Alert severity="success" icon={<CheckCircleIcon />} className="ws-cid-alert-mb">
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Incident Created Successfully!
              </Typography>
              <Typography variant="body2">
                Incident Number: <strong>{createdIncident.incidentNumber}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Due Date: {new Date(createdIncident.dueDate).toLocaleDateString()}
              </Typography>
            </Alert>
          )}

          <Paper elevation={0} className="ws-cid-job-info">
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Job Information
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Chip label={`File: ${job.fileName}`} size="small" />
              <Chip label={`Country: ${job.country}`} size="small" color="primary" />
              <Chip
                label={`Success: ${job.successRows} | Failed: ${job.failedRows}`}
                size="small"
                color="info"
              />
              <Chip
                label={`Success Rate: ${successRate.toFixed(1)}%`}
                size="small"
                color={
                  isSuccessRateBlocked ? 'error' : isSuccessRateWarning ? 'warning' : 'success'
                }
              />
            </Stack>
          </Paper>

          {isSuccessRateBlocked && !createdIncident && (
            <Alert severity="error" className="ws-cid-alert-mb">
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Success Rate Too Low - Incident Creation Blocked
              </Typography>
              <Typography variant="body2">
                This job has a success rate of only <strong>{successRate.toFixed(1)}%</strong>.
                ServiceNow incidents cannot be created for jobs with less than{' '}
                <strong>40% success rate</strong>.
              </Typography>
              <Typography variant="body2" className="ws-cid-note-mt">
                <strong>Current results:</strong> {job.successRows} succeeded, {job.failedRows}{' '}
                failed out of {totalRows} total rows.
              </Typography>
            </Alert>
          )}

          {isSuccessRateWarning && !createdIncident && (
            <Alert severity="warning" className="ws-cid-alert-mb">
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Low Success Rate Warning
              </Typography>
              <Typography variant="body2">
                This job has a success rate of <strong>{successRate.toFixed(1)}%</strong>.
                <br />
                While you can create an incident, please note that optimal results should have at
                <strong> 60% success rate</strong>.
              </Typography>
              <Typography variant="body2" className="ws-cid-note-mt">
                <strong>Current results:</strong> {job.successRows} succeeded, {job.failedRows}{' '}
                failed out of {totalRows} total rows.
              </Typography>
            </Alert>
          )}

          {!createdIncident && (
            <>
              <Typography variant="subtitle2" gutterBottom className="ws-cid-subtitle-mb">
                Incident Details
              </Typography>

              <div className="ws-cid-fields-row">
                <div className="ws-cid-field-col">
                  <Chip label={job.country} variant="outlined" className="ws-cid-country-chip" />
                </div>

                <div className="ws-cid-task-col">
                  <TextField
                    label="Argos Task Number *"
                    value={taskNumber}
                    onChange={e => handleTaskNumberChange(e.target.value)}
                    size="small"
                    fullWidth
                    placeholder="20240626000006813"
                    error={!!errors.taskNumber}
                    helperText={errors.taskNumber || ''}
                    className="ws-cid-field-40"
                  />
                </div>

                <div className="ws-cid-spacer" />

                <div className="ws-cid-deadline-col">
                  <DatePicker
                    label="Deadline *"
                    value={deadline}
                    onChange={newValue => {
                      setDeadline(newValue);
                      if (errors.deadline) {
                        const newErrors = { ...errors };
                        delete newErrors.deadline;
                        setErrors(newErrors);
                      }
                    }}
                    format="dd.MM.yyyy"
                    minDate={new Date()}
                    slotProps={{
                      textField: {
                        size: 'small',
                        error: !!errors.deadline,
                        helperText: errors.deadline || `${businessDaysCount} business days`,
                        className: 'ws-cid-deadline-picker',
                      },
                    }}
                  />
                </div>
              </div>

              <TextField
                label="Incident Description *"
                multiline
                rows={12}
                fullWidth
                value={description}
                onChange={e => {
                  setDescription(e.target.value);
                  if (errors.description) {
                    const newErrors = { ...errors };
                    delete newErrors.description;
                    setErrors(newErrors);
                  }
                }}
                placeholder="Incident description..."
                error={!!errors.description}
                helperText={errors.description || 'You can edit the default description above'}
                className="ws-cid-desc-mb"
              />

              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Note:</strong> The error tab (2nd sheet) will be automatically removed
                  from the Excel file before attaching to the incident.
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>

        <DialogActions className="ws-cid-actions">
          <Button onClick={handleClose} disabled={loading}>
            {createdIncident ? 'Close' : 'Cancel'}
          </Button>

          {createdIncident ? (
            <Button
              variant="contained"
              color="success"
              onClick={handleOpenIncident}
              startIcon={<OpenInNewIcon />}
            >
              Open in ServiceNow
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleCreateIncident}
              startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
              disabled={loading || isSuccessRateBlocked}
            >
              {loading ? 'Creating...' : 'Create Incident'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CreateIncidentDialog;
